import { useState, useMemo, useEffect, type FormEvent } from 'react';
import {
  Box, Typography, Paper, Stack, Button, IconButton, TextField, MenuItem, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, Chip,
} from '@mui/material';
import {
  ChevronLeftOutlined, ChevronRightOutlined, DeleteOutlineOutlined,
  EventAvailableOutlined,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import type { Entry, Shift, ShiftKind, User } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';

// Rolling 15-day view: yesterday + today + next 13 days.
const WINDOW_DAYS = 15;
const defaultRangeStart = () => dayjs().subtract(1, 'day').startOf('day');

export default function Calendar() {
  const [rangeStart, setRangeStart] = useState<Dayjs>(defaultRangeStart);
  const [editing, setEditing] = useState<Shift | null>(null);
  const [open, setOpen] = useState(false);
  const [apptEditing, setApptEditing] = useState<ApptDraft | null>(null);
  const [apptOpen, setApptOpen] = useState(false);

  const from = rangeStart.toISOString();
  const to = rangeStart.add(WINDOW_DAYS, 'day').toISOString();

  const shiftsQuery = useQuery({
    queryKey: ['shifts', { from, to }],
    queryFn: () => api.get<Shift[]>(`/api/shifts?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });

  const appointmentsQuery = useQuery({
    queryKey: ['entries', { category: 'appointment', since: from, until: to }],
    queryFn: () => api.get<Entry[]>(
      `/api/entries?category=appointment&since=${encodeURIComponent(from)}&until=${encodeURIComponent(to)}&limit=200`,
    ),
  });

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<User[]>('/api/users'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/shifts/${id}`),
    onSuccess: () => shiftsQuery.refetch(),
  });

  const qc = useQueryClient();
  const deleteApptMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/entries/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entries'] });
      appointmentsQuery.refetch();
    },
  });

  const days = useMemo(
    () => Array.from({ length: WINDOW_DAYS }, (_, i) => rangeStart.add(i, 'day')),
    [rangeStart],
  );

  // Multi-day shifts (e.g. nurse on-call from Fri 12:00 to Sun 13:00) need to
  // appear on every day they span — not just their start date. Walk from the
  // shift's start day to its end day inclusive and push to each bucket.
  const shiftsByDay = useMemo(() => {
    const map: Record<string, Shift[]> = {};
    for (const s of shiftsQuery.data ?? []) {
      const start = dayjs(s.startAt).startOf('day');
      const end = dayjs(s.endAt).startOf('day');
      // If endAt lands exactly at midnight (00:00) on the next day we treat it
      // as ending on the previous day — otherwise a shift "Mon 09:00 → Tue
      // 00:00" would render on both Mon and Tue.
      const endExclusive = dayjs(s.endAt).isSame(end, 'minute') && dayjs(s.endAt).hour() === 0
        ? end.subtract(1, 'day')
        : end;
      let cursor = start;
      while (!cursor.isAfter(endExclusive, 'day')) {
        const key = cursor.format('YYYY-MM-DD');
        (map[key] ??= []).push(s);
        cursor = cursor.add(1, 'day');
      }
    }
    return map;
  }, [shiftsQuery.data]);

  const appointmentsByDay = useMemo(() => {
    const map: Record<string, Entry[]> = {};
    for (const a of appointmentsQuery.data ?? []) {
      const key = dayjs(a.occurredAt).format('YYYY-MM-DD');
      (map[key] ??= []).push(a);
    }
    // Sort each day asc by time so earliest appointments render first.
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => dayjs(a.occurredAt).valueOf() - dayjs(b.occurredAt).valueOf());
    }
    return map;
  }, [appointmentsQuery.data]);

  const openNew = (day: Dayjs) => {
    setEditing({
      id: '',
      startAt: day.hour(9).minute(0).toISOString(),
      endAt: day.hour(13).minute(0).toISOString(),
      kind: 'family',
      assigneeId: null,
      assigneeName: null,
      label: '',
      notes: null,
      createdById: '',
      createdByName: '',
      createdAt: new Date().toISOString(),
    });
    setOpen(true);
  };

  const openNewAppointment = (day: Dayjs) => {
    setApptEditing({
      id: null,
      occurredAt: day.hour(10).minute(0).toISOString(),
      title: '',
      body: '',
    });
    setApptOpen(true);
  };

  const openEditAppointment = (e: Entry) => {
    setApptEditing({
      id: e.id,
      occurredAt: e.occurredAt,
      title: e.title ?? '',
      body: e.body,
    });
    setApptOpen(true);
  };

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Calendar</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <IconButton onClick={() => setRangeStart((r) => r.subtract(7, 'day'))}>
            <ChevronLeftOutlined />
          </IconButton>
          <Button onClick={() => setRangeStart(defaultRangeStart())}>Jump to today</Button>
          <IconButton onClick={() => setRangeStart((r) => r.add(7, 'day'))}>
            <ChevronRightOutlined />
          </IconButton>
        </Stack>
      </Stack>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        {rangeStart.format('DD MMM')} – {rangeStart.add(WINDOW_DAYS - 1, 'day').format('DD MMM YYYY')}
      </Typography>

      <Stack spacing={1.5}>
        {days.map((d) => {
          const key = d.format('YYYY-MM-DD');
          const dayShifts = shiftsByDay[key] ?? [];
          const dayAppts = appointmentsByDay[key] ?? [];
          const isToday = d.isSame(dayjs(), 'day');
          const empty = dayShifts.length === 0 && dayAppts.length === 0;
          return (
            <Paper
              key={key}
              variant="outlined"
              sx={{ p: 2, bgcolor: isToday ? 'action.hover' : 'background.paper' }}
            >
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {d.format('dddd')}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {d.format('DD MMM')}
                  </Typography>
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" onClick={() => openNewAppointment(d)}>+ Appointment</Button>
                  <Button size="small" onClick={() => openNew(d)}>+ Shift</Button>
                </Stack>
              </Stack>
              {empty ? (
                <Typography variant="caption" color="text.secondary">Nothing scheduled</Typography>
              ) : (
                <Stack spacing={1}>
                  {dayAppts.map((a) => (
                    <Paper
                      key={a.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderLeft: 3,
                        borderLeftColor: 'warning.main',
                        cursor: 'pointer',
                      }}
                      onClick={() => openEditAppointment(a)}
                    >
                      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                          <EventAvailableOutlined fontSize="small" sx={{ mt: 0.25, color: 'warning.main' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {dayjs(a.occurredAt).format('HH:mm')}
                              {a.title ? ` · ${a.title}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                              {a.body}
                            </Typography>
                          </Box>
                        </Stack>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); deleteApptMutation.mutate(a.id); }}
                          aria-label="delete appointment"
                        >
                          <DeleteOutlineOutlined fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                  {dayShifts.map((s) => (
                    <Paper
                      key={s.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderLeft: 3,
                        borderLeftColor: kindColor(s.kind),
                        cursor: 'pointer',
                      }}
                      onClick={() => { setEditing(s); setOpen(true); }}
                    >
                      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatShiftRange(s, d)} · {s.label}
                          </Typography>
                          {s.assigneeName && (
                            <Typography variant="caption" color="text.secondary">
                              {s.assigneeName} · {s.kind}
                            </Typography>
                          )}
                          {s.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {s.notes}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(s.id); }}
                        >
                          <DeleteOutlineOutlined fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>

      <ShiftDialog
        open={open}
        shift={editing}
        users={usersQuery.data ?? []}
        onClose={() => { setOpen(false); setEditing(null); }}
        onSaved={() => {
          setOpen(false);
          setEditing(null);
          shiftsQuery.refetch();
        }}
      />

      <AppointmentDialog
        open={apptOpen}
        appt={apptEditing}
        onClose={() => { setApptOpen(false); setApptEditing(null); }}
        onSaved={() => {
          setApptOpen(false);
          setApptEditing(null);
          appointmentsQuery.refetch();
        }}
      />
    </Box>
  );
}

type ApptDraft = {
  id: string | null;
  occurredAt: string;
  title: string;
  body: string;
};

function kindColor(kind: ShiftKind): string {
  if (kind === 'nurse') return 'secondary.main';
  if (kind === 'family') return 'primary.main';
  return 'text.secondary';
}

// Render the time portion of a shift relative to the day-card it's appearing
// in. A multi-day shift shows "12:00 – ongoing", "all day", or "until 13:00"
// on the relevant day so a glance at one card tells you what's happening
// *that* day specifically.
function formatShiftRange(s: Shift, day: Dayjs): string {
  const start = dayjs(s.startAt);
  const end = dayjs(s.endAt);
  const startsToday = start.isSame(day, 'day');
  const endsToday = end.isSame(day, 'day');
  if (startsToday && endsToday) {
    return `${start.format('HH:mm')} – ${end.format('HH:mm')}`;
  }
  if (startsToday) {
    return `${start.format('HH:mm')} – ongoing (until ${end.format('DD MMM HH:mm')})`;
  }
  if (endsToday) {
    return `until ${end.format('HH:mm')} (from ${start.format('DD MMM HH:mm')})`;
  }
  return `all day (${start.format('DD MMM HH:mm')} → ${end.format('DD MMM HH:mm')})`;
}

// Quick presets — tap a chip, start/end jump to that slot on the current day.
const TIME_SLOTS = [
  { label: 'Morning',   startH: 9,  endH: 12 },
  { label: 'Afternoon', startH: 13, endH: 17 },
  { label: 'Night',     startH: 17, endH: 21 },
] as const;

const LABEL_PRESETS = [
  'With Mum',
  'Meds check',
  'Meal prep',
  'Overnight',
  'Nurse visit',
  'GP/appointment',
  'Shopping',
  'Respite for Dad',
] as const;

const pad = (n: number) => String(n).padStart(2, '0');

function ShiftDialog({
  open, shift, users, onClose, onSaved,
}: {
  open: boolean;
  shift: Shift | null;
  users: User[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const isNew = !shift?.id;
  const [startAt, setStartAt] = useState<Dayjs | null>(shift ? dayjs(shift.startAt) : null);
  const [endAt, setEndAt] = useState<Dayjs | null>(shift ? dayjs(shift.endAt) : null);
  const [kind, setKind] = useState<ShiftKind>(shift?.kind ?? 'family');
  const [assigneeId, setAssigneeId] = useState<string>(shift?.assigneeId ?? '');
  const [label, setLabel] = useState<string>(shift?.label ?? '');
  const [notes, setNotes] = useState<string>(shift?.notes ?? '');

  // Re-sync when opening a different shift
  useEffect(() => {
    if (shift) {
      setStartAt(dayjs(shift.startAt));
      setEndAt(dayjs(shift.endAt));
      setKind(shift.kind);
      setAssigneeId(shift.assigneeId ?? '');
      setLabel(shift.label);
      setNotes(shift.notes ?? '');
    }
  }, [shift]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        startAt: startAt!.toISOString(),
        endAt: endAt!.toISOString(),
        kind,
        assigneeId: assigneeId || null,
        label,
        notes: notes.trim() || null,
      };
      if (isNew) return api.post<Shift>('/api/shifts', payload);
      return api.patch<Shift>(`/api/shifts/${shift!.id}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] });
      onSaved();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!startAt || !endAt || !label.trim()) return;
    mutation.mutate();
  };

  // Anchor the time-slot chips to the day of whichever date picker currently
  // has a value (start first, then end, then today).
  const applySlot = (startH: number, endH: number) => {
    const anchor = startAt ?? endAt ?? dayjs();
    const day = anchor.startOf('day');
    setStartAt(day.hour(startH).minute(0).second(0).millisecond(0));
    setEndAt(day.hour(endH).minute(0).second(0).millisecond(0));
  };

  const slotIsActive = (startH: number, endH: number) =>
    startAt?.hour() === startH && startAt?.minute() === 0
    && endAt?.hour() === endH && endAt?.minute() === 0
    && startAt?.isSame(endAt, 'day');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNew ? 'Add shift' : 'Edit shift'}</DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {TIME_SLOTS.map((slot) => {
                const active = slotIsActive(slot.startH, slot.endH);
                return (
                  <Chip
                    key={slot.label}
                    label={`${slot.label} · ${pad(slot.startH)}:00–${pad(slot.endH)}:00`}
                    onClick={() => applySlot(slot.startH, slot.endH)}
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    clickable
                  />
                );
              })}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DateTimePicker
                label="Start"
                value={startAt}
                onChange={setStartAt}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DateTimePicker
                label="End"
                value={endAt}
                onChange={setEndAt}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Stack>
            <TextField
              select
              size="small"
              label="Kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as ShiftKind)}
            >
              <MenuItem value="family">Family</MenuItem>
              <MenuItem value="nurse">Nurse</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Assignee"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
            >
              <MenuItem value="">— unassigned —</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </TextField>
            <Stack spacing={1}>
              <TextField
                label="Label"
                size="small"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
                placeholder="e.g. Afternoon with Mum"
              />
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {LABEL_PRESETS.map((preset) => (
                  <Chip
                    key={preset}
                    label={preset}
                    size="small"
                    onClick={() => setLabel(preset)}
                    color={label === preset ? 'primary' : 'default'}
                    variant={label === preset ? 'filled' : 'outlined'}
                    clickable
                  />
                ))}
              </Stack>
            </Stack>
            <TextField
              label="Notes"
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending || !label.trim()}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

// Quick presets for common appointment types — prefills the title.
const APPT_PRESETS = [
  'GP',
  'Oncologist',
  'Pathology',
  'Scan / imaging',
  'Hospital',
  'Palliative nurse',
  'Physio',
] as const;

function AppointmentDialog({
  open, appt, onClose, onSaved,
}: {
  open: boolean;
  appt: ApptDraft | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const isNew = !appt?.id;
  const [occurredAt, setOccurredAt] = useState<Dayjs | null>(appt ? dayjs(appt.occurredAt) : null);
  const [title, setTitle] = useState(appt?.title ?? '');
  const [body, setBody] = useState(appt?.body ?? '');

  useEffect(() => {
    if (appt) {
      setOccurredAt(dayjs(appt.occurredAt));
      setTitle(appt.title);
      setBody(appt.body);
    }
  }, [appt]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        category: 'appointment' as const,
        occurredAt: occurredAt!.toISOString(),
        title: title.trim() || null,
        body: body.trim(),
      };
      if (isNew) return api.post('/api/entries', payload);
      return api.patch(`/api/entries/${appt!.id}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entries'] });
      onSaved();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!occurredAt || !body.trim()) return;
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNew ? 'Add appointment' : 'Edit appointment'}</DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <DateTimePicker
              label="When"
              value={occurredAt}
              onChange={setOccurredAt}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <Stack spacing={1}>
              <TextField
                label="Title"
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Dr Patel · oncology"
              />
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {APPT_PRESETS.map((preset) => (
                  <Chip
                    key={preset}
                    label={preset}
                    size="small"
                    onClick={() => setTitle(preset)}
                    color={title === preset ? 'primary' : 'default'}
                    variant={title === preset ? 'filled' : 'outlined'}
                    clickable
                  />
                ))}
              </Stack>
            </Stack>
            <TextField
              label="Details"
              multiline
              minRows={3}
              required
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Address, notes, who's taking her, things to bring…"
            />
            {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending || !body.trim() || !occurredAt}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
